if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));

        this.cart =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');

        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();

        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');

        const spinner = this.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');

        const formData = new FormData(this.form);

        try {
          /*
           * ============================================================
           * STEP 1
           * Save cart state BEFORE adding the new item.
           * ============================================================
           */
          const cartBefore = await this.getCurrentCart();

          /*
           * ============================================================
           * STEP 2
           * Add item normally through Shopify / REZ.
           *
           * REZ adds:
           * _rez_preorder_campaign_id
           *
           * to the line item AFTER it enters the cart.
           * ============================================================
           */
          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];

          if (this.cart) {
            formData.append(
              'sections',
              this.cart.getSectionsToRender().map((section) => section.id)
            );

            formData.append('sections_url', window.location.pathname);
            this.cart.setActiveElement(document.activeElement);
          }

          config.body = formData;

          const addResponse = await fetch(`${routes.cart_add_url}`, config);
          const addedItem = await addResponse.json();

          /*
           * Normal Shopify add-to-cart error
           */
          if (addedItem.status) {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              errors: addedItem.errors || addedItem.description,
              message: addedItem.message,
            });

            this.handleErrorMessage(
              addedItem.description || addedItem.message
            );

            const soldOutMessage =
              this.submitButton.querySelector('.sold-out-message');

            if (soldOutMessage) {
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
            }

            this.error = true;
            return;
          }

          /*
           * ============================================================
           * STEP 3
           * Fetch cart AFTER REZ has added its campaign ID.
           * ============================================================
           */
          const cartAfter = await this.getCurrentCart();

          /*
           * ============================================================
           * STEP 4
           * Validate entire cart.
           * ============================================================
           */
          const validation = this.validateRezCart(cartAfter.items || []);

          /*
           * ============================================================
           * STEP 5
           * Invalid combination:
           *
           * Restore the line that was just added to its previous quantity.
           * This is safer than simply deleting it because the customer
           * may already have had the same line item in the cart.
           * ============================================================
           */
          if (!validation.allowed) {
            await this.restoreAddedItem(addedItem, cartBefore);

            this.handleErrorMessage(validation.message);

            this.error = true;

            /*
             * Refresh cart drawer/cart notification so the rejected
             * item disappears immediately.
             */
            await this.refreshCartDisplay();

            return;
          }

          /*
           * ============================================================
           * VALID CART
           * ============================================================
           */

          this.error = false;

          if (!this.cart) {
            window.location = window.routes.cart_url;
            return;
          }

          const startMarker = CartPerformance.createStartingMarker(
            'add:wait-for-subscribers'
          );

          await publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: formData.get('id'),
            cartData: addedItem,
          });

          CartPerformance.measureFromMarker(
            'add:wait-for-subscribers',
            startMarker
          );

          const quickAddModal = this.closest('quick-add-modal');

          if (quickAddModal) {
            document.body.addEventListener(
              'modalClosed',
              () => {
                setTimeout(() => {
                  CartPerformance.measure(
                    'add:paint-updated-sections',
                    () => {
                      this.cart.renderContents(addedItem);
                    }
                  );
                });
              },
              { once: true }
            );

            quickAddModal.hide(true);
          } else {
            CartPerformance.measure(
              'add:paint-updated-sections',
              () => {
                this.cart.renderContents(addedItem);
              }
            );
          }
        } catch (error) {
          console.error('Preorder cart validation error:', error);

          this.handleErrorMessage(
            'Something went wrong. Please try again.'
          );

          this.error = true;
        } finally {
          this.submitButton.classList.remove('loading');

          if (
            this.cart &&
            this.cart.classList.contains('is-empty')
          ) {
            this.cart.classList.remove('is-empty');
          }

          /*
           * Validation errors must NOT permanently disable Add to Cart.
           */
          this.submitButton.removeAttribute('aria-disabled');

          if (spinner) spinner.classList.add('hidden');

          CartPerformance.measureFromEvent(
            'add:user-action',
            evt
          );
        }
      }

      /*
       * ================================================================
       * GET CURRENT SHOPIFY CART
       * ================================================================
       */
      async getCurrentCart() {
        const root = window.Shopify?.routes?.root || '/';

        const response = await fetch(`${root}cart.js`, {
          method: 'GET',
          headers: {
            Accept: 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error('Unable to load the current cart.');
        }

        return await response.json();
      }

      /*
       * ================================================================
       * CART RULES
       *
       * Regular + Regular
       *     ALLOWED
       *
       * Regular + Preorder
       *     BLOCKED
       *
       * Preorder Campaign A + Campaign A
       *     ALLOWED
       *
       * Preorder Campaign A + Campaign B
       *     BLOCKED
       * ================================================================
       */
      validateRezCart(cartItems) {
        let hasRegularItem = false;
        let hasPreorderItem = false;

        const campaignIds = new Set();

        cartItems.forEach((item) => {
          const campaignId =
            item.properties?.['_rez_preorder_campaign_id'];

          if (campaignId) {
            hasPreorderItem = true;
            campaignIds.add(String(campaignId));
          } else {
            hasRegularItem = true;
          }
        });

        /*
         * Regular + Preorder
         */
        if (hasRegularItem && hasPreorderItem) {
          return {
            allowed: false,
            message:
              'Pre-order items and in-stock items must be purchased separately. Please place separate orders.',
          };
        }

        /*
         * Multiple REZ preorder campaigns
         */
        if (campaignIds.size > 1) {
          return {
            allowed: false,
            message:
              'Pre-order items with different release dates must be purchased separately. Please place a separate order for each release.',
          };
        }

        /*
         * Everything else is allowed.
         */
        return {
          allowed: true,
        };
      }

      /*
       * ================================================================
       * RESTORE ITEM IF CART VALIDATION FAILS
       *
       * Example:
       *
       * Cart before:
       * Campaign A CD = quantity 1
       *
       * Customer tries adding prohibited item.
       *
       * Instead of blindly deleting the line,
       * restore its quantity to what existed before.
       * ================================================================
       */
      async restoreAddedItem(addedItem, cartBefore) {
        if (!addedItem || !addedItem.key) {
          console.error(
            'Unable to restore rejected cart item because no line item key was returned.'
          );
          return;
        }

        const previousLine = (cartBefore.items || []).find(
          (item) => item.key === addedItem.key
        );

        const previousQuantity = previousLine
          ? previousLine.quantity
          : 0;

        const root = window.Shopify?.routes?.root || '/';

        const response = await fetch(`${root}cart/change.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            id: addedItem.key,
            quantity: previousQuantity,
          }),
        });

        if (!response.ok) {
          throw new Error(
            'Unable to restore the cart after preorder validation.'
          );
        }

        return await response.json();
      }

      /*
       * ================================================================
       * REFRESH CART DRAWER / CART NOTIFICATION
       * ================================================================
       */
      async refreshCartDisplay() {
        if (!this.cart) return;

        try {
          /*
           * Ride cart drawer
           */
          if (
            typeof this.cart.renderContents === 'function' &&
            typeof this.cart.getSectionsToRender === 'function'
          ) {
            const root = window.Shopify?.routes?.root || '/';

            const sectionIds = this.cart
              .getSectionsToRender()
              .map((section) => section.id)
              .join(',');

            const response = await fetch(
              `${root}?sections=${sectionIds}`
            );

            const sections = await response.json();

            const cartData = await this.getCurrentCart();

            cartData.sections = sections;

            this.cart.renderContents(cartData);

            return;
          }

          /*
           * Fallback:
           * Tell Ride cart components to reload.
           */
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form-validation',
            cartData: await this.getCurrentCart(),
          });
        } catch (error) {
          console.error('Unable to refresh cart display:', error);
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(
            '.product-form__error-message-wrapper'
          );

        if (!this.errorMessageWrapper) return;

        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            '.product-form__error-message'
          );

        this.errorMessageWrapper.toggleAttribute(
          'hidden',
          !errorMessage
        );

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute(
            'disabled',
            'disabled'
          );

          if (text) {
            this.submitButtonText.textContent = text;
          }
        } else {
          this.submitButton.removeAttribute('disabled');

          this.submitButtonText.textContent =
            window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}